import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  LifeBuoy,
  MessageSquarePlus,
  School,
  User,
  Phone,
  Mail,
  Clock,
  CheckCircle2,
  AlertCircle,
  Send,
  Loader2,
  Calendar,
} from "lucide-react";
import { formatDateTimeVN } from "../../../utils/formatUtils";
import { updateAdminTeacherSupport } from "../../../api/crm/teacherSupport/teacherSupportApi";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";

const CATEGORY_LABELS = {
  login: "Đăng nhập / thiết bị",
  attendance: "Điểm danh",
  schedule: "Thời khóa biểu",
  materials: "Học liệu / bài giảng",
  account: "Tài khoản",
  other: "Khác",
};

const STATUS_OPTIONS = [
  { value: "open", label: "Đã gửi (Chờ xử lý)", color: "amber" },
  { value: "in_progress", label: "Đang xử lý", color: "blue" },
  { value: "resolved", label: "Đã xử lý", color: "emerald" },
  { value: "closed", label: "Đã đóng", color: "gray" },
];

export default function TeacherSupportDetailModal({
  isOpen,
  onClose,
  ticket,
  onUpdated,
}) {
  const [status, setStatus] = useState("open");
  const [adminNote, setAdminNote] = useState("");
  const [saving, setSaving] = useState(false);

  const canUpdate = hasPermission("crm.teacher_accounts", "U");

  useEffect(() => {
    if (ticket) {
      setStatus(ticket.status || "open");
      setAdminNote(ticket.admin_note || "");
    }
  }, [ticket]);

  if (!isOpen || !ticket) return null;

  const isFeedback = ticket.request_type === "feedback";
  const typeLabel = isFeedback ? "Góp ý" : "Yêu cầu hỗ trợ";

  const handleSave = async () => {
    if (!canUpdate) {
      return showErrorToast("Bạn không có quyền cập nhật trạng thái phiếu hỗ trợ.");
    }
    try {
      setSaving(true);
      await updateAdminTeacherSupport(ticket.id, {
        status,
        admin_note: adminNote.trim() || null,
      });
      showSuccessToast("Đã lưu phản hồi thành công!");
      if (onUpdated) onUpdated();
      onClose();
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || "Không thể cập nhật phản hồi. Vui lòng thử lại."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col max-h-[92vh]"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/70 px-6 py-4">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                  isFeedback
                    ? "bg-purple-100 text-purple-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {isFeedback ? (
                  <MessageSquarePlus className="h-5 w-5" />
                ) : (
                  <LifeBuoy className="h-5 w-5" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-gray-900">
                    Chi tiết {typeLabel.toLowerCase()}
                  </h3>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      ticket.status === "open"
                        ? "bg-amber-50 text-amber-800 border border-amber-200"
                        : ticket.status === "in_progress"
                        ? "bg-blue-50 text-blue-800 border border-blue-200"
                        : ticket.status === "resolved"
                        ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                        : "bg-gray-100 text-gray-700 border border-gray-200"
                    }`}
                  >
                    {STATUS_OPTIONS.find((s) => s.value === ticket.status)?.label ||
                      ticket.status}
                  </span>
                </div>
                <p className="text-xs text-gray-400">
                  Mã phiếu: <span className="font-mono">{ticket.id}</span>
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Thông tin giáo viên & Trường */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 rounded-xl border border-gray-100 bg-gray-50/50 p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-700">
                  <User className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                    Giáo viên
                  </p>
                  <p className="text-sm font-bold text-gray-900 truncate">
                    {ticket.teacher_name || "—"}
                  </p>
                  <p className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
                    <Mail className="h-3 w-3 shrink-0" />
                    {ticket.teacher_email || "—"}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-700">
                  <School className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                    Trường / Đơn vị
                  </p>
                  <p className="text-sm font-bold text-gray-900 truncate">
                    {ticket.school_name || "Chưa gán trường"}
                  </p>
                  <p className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
                    <Phone className="h-3 w-3 shrink-0" />
                    {ticket.contact_phone || ticket.teacher_phone || "—"}
                  </p>
                </div>
              </div>
            </div>

            {/* Nội dung yêu cầu */}
            <div className="rounded-xl border border-gray-100 p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2.5">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500">
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                  Danh mục:{" "}
                  <strong className="text-gray-800">
                    {CATEGORY_LABELS[ticket.category] || ticket.category}
                  </strong>
                </span>
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  Gửi lúc: {formatDateTimeVN(ticket.created_at)}
                </span>
              </div>

              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-1">
                  {ticket.subject}
                </h4>
                <div className="rounded-lg bg-gray-50/80 p-3 text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed border border-gray-100">
                  {ticket.content}
                </div>
              </div>
            </div>

            {/* Phần xử lý & phản hồi */}
            <div className="rounded-xl border border-red-100 bg-red-50/20 p-4 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-gray-800">
                <CheckCircle2 className="h-4 w-4 text-red-700" />
                Xử lý & Phản hồi tới giáo viên
              </div>

              {/* Trạng thái */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Trạng thái phiếu
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {STATUS_OPTIONS.map((opt) => {
                    const isSelected = status === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setStatus(opt.value)}
                        className={`px-3 py-2 text-xs font-bold rounded-xl border transition-all text-center ${
                          isSelected
                            ? "bg-red-700 text-white border-red-700 shadow-sm"
                            : "bg-white text-gray-700 border-gray-200 hover:border-red-300"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Ghi chú phản hồi */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Nội dung phản hồi (Thầy/Cô sẽ nhìn thấy trên app IMA Lesson)
                </label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  rows={3}
                  placeholder="Nhập nội dung trả lời / giải thích / hướng dẫn xử lý cho giáo viên..."
                  className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-800 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition"
                />
              </div>

              {/* Thông tin người xử lý gần nhất nếu có */}
              {ticket.resolved_at && (
                <div className="flex items-center gap-2 text-xs text-emerald-700 font-medium pt-1">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  <span>
                    Xử lý bởi:{" "}
                    <strong>{ticket.resolver_name || "Quản trị viên"}</strong>{" "}
                    vào {formatDateTimeVN(ticket.resolved_at)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-bold text-gray-700 hover:bg-gray-100 transition"
            >
              Đóng
            </button>

            {canUpdate && (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl bg-red-700 px-5 py-2 text-xs font-bold uppercase tracking-wider text-white shadow-sm hover:bg-red-800 active:scale-95 transition disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Lưu & Cập nhật
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
